import { apiFetch, API_BASE } from './api';
import type { ApiEnvId } from './api-pterodactyl-config.service';

/**
 * Passthrough du File Explorer de Pterodactyl pour une API sur un env.
 * Les endpoints backend délèguent tels quels aux endpoints Ptero Client API.
 * Réponses au format Ptero (voir doc Pterodactyl "Client API - Files").
 */
export interface PteroFileNode {
  name: string;
  mode: string;
  mode_bits?: string;
  size: number;
  is_file: boolean;
  is_symlink: boolean;
  mimetype?: string;
  created_at: string;
  modified_at: string;
}

interface PteroListResp {
  object: 'list';
  data: Array<{ object: 'file_object'; attributes: PteroFileNode }>;
}

const base = (pid: string, apiId: string) => `/api/projects/${pid}/apis/${apiId}/files`;

/**
 * Query commune : `instance` n'est ajouté que hors Principale, pour qu'un CICD plus ancien
 * reçoive exactement les mêmes appels qu'avant.
 */
function query(env: ApiEnvId, instance: string | undefined, extra: Record<string, string> = {}): URLSearchParams {
  const params = new URLSearchParams({ env });
  if (instance && instance !== 'default') params.set('instance', instance);
  for (const [k, v] of Object.entries(extra)) params.set(k, v);
  return params;
}

async function authHeader(): Promise<Record<string, string>> {
  const { tokenStorage } = await import('./api');
  const t = tokenStorage.get();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// NOTE : API_BASE réutilisé depuis ./api pour rester sur VITE_DEPLOY_API_URL.

export const apiFilesService = {
  /** Liste le contenu d'un dossier sur le serveur Ptero de l'API. */
  list: async (pid: string, apiId: string, env: ApiEnvId, directory = '/', instance?: string): Promise<PteroFileNode[]> => {
    const params = query(env, instance, { directory });
    const resp = await apiFetch<PteroListResp | { data: unknown[] }>(`${base(pid, apiId)}/list?${params}`);
    // Ptero renvoie { object:'list', data:[ {object:'file_object', attributes:{...}} ] }
    if (resp && Array.isArray((resp as PteroListResp).data)) {
      return (resp as PteroListResp).data.map((n) => n.attributes);
    }
    return [];
  },

  /** Renvoie le contenu texte brut d'un fichier. */
  read: async (pid: string, apiId: string, env: ApiEnvId, file: string, instance?: string): Promise<string> => {
    const params = query(env, instance, { file });
    const url = `${API_BASE}${base(pid, apiId)}/contents?${params}`;
    const headers = { Accept: 'text/plain', ...(await authHeader()) };
    const res = await fetch(url, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.text();
  },

  /**
   * Écrit le contenu d'un fichier. Crée si absent.
   * `content` accepte du texte OU du binaire (Blob/File/ArrayBuffer) : un File est streamé
   * tel quel par fetch (pas de chargement mémoire complet, OK pour de gros .dll). Utilisé
   * par l'éditeur inline (texte) et par le déploiement direct de DLL en dev (binaire).
   */
  write: async (pid: string, apiId: string, env: ApiEnvId, file: string, content: string | ArrayBuffer | Blob, instance?: string): Promise<void> => {
    const params = query(env, instance, { file });
    const url = `${API_BASE}${base(pid, apiId)}/write?${params}`;
    const headers = { 'Content-Type': 'application/octet-stream', ...(await authHeader()) };
    const res = await fetch(url, { method: 'POST', headers, body: content });
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  },

  createFolder: (pid: string, apiId: string, env: ApiEnvId, root: string, name: string, instance?: string) =>
    apiFetch<void>(`${base(pid, apiId)}/create-folder?${query(env, instance)}`, {
      method: 'POST', json: { root, name },
    }),

  rename: (pid: string, apiId: string, env: ApiEnvId, root: string, from: string, to: string, instance?: string) =>
    apiFetch<void>(`${base(pid, apiId)}/rename?${query(env, instance)}`, {
      method: 'PUT', json: { root, from, to },
    }),

  copy: (pid: string, apiId: string, env: ApiEnvId, location: string, instance?: string) =>
    apiFetch<void>(`${base(pid, apiId)}/copy?${query(env, instance)}`, {
      method: 'POST', json: { location },
    }),

  deletePaths: (pid: string, apiId: string, env: ApiEnvId, root: string, files: string[], instance?: string) =>
    apiFetch<void>(`${base(pid, apiId)}/delete?${query(env, instance)}`, {
      method: 'POST', json: { root, files },
    }),
};
